import axios from 'axios'
import approx from 'approximate-number'
import { buildApiUrl } from '@/components/api-url-builder'

export class AggregateHelper {

  constructor(changeCallback, errorCallback, workingCallback) {
    this._changeCallback = changeCallback
    this._errorCallback = errorCallback
    this._workingCallback = workingCallback
    this._data = null    

    this._assetCountData = null
    this._assetCountLegend = null
    this._mtbfData = null
    this._mtbfLegend = null
    this._consData = null
    this._consLegend = null
  }

  get Data() {
    return this._data;
  }

  PrepData() {
    this.PrepAssetCountData()    
    this.PrepMtbfData()
    this.PrepConsData()
  }

  PrepConsData() {
    this._consLegend = null
    this._consData = null

    let processedSeries = [{
      name: '',
      type:'bar',              
      data: [],
      avoidLabelOverlap: false,
      animationType: 'scale',
      animationEasing: 'elasticOut'
    }]

    let processedLegend = [];
    if(this._data) {      
      for(const key of Object.keys(this._data)) {
        if(key === 'totalCount' || key === 'totalImprovementCount')
          continue

        if(this._data[key].count > 0 && this._data[key].consAvg > 0)
          processedSeries[0].data.push({ name: this._data[key].caption, value:this._data[key].consAvg, row: this._data[key] })                
      }

      processedSeries[0].data.sort((a,b) => a.value === b.value ? 0 : (a.value > b.value ? -1 : 1))
      if(processedSeries[0].data.length > 25)
        processedSeries[0].data.splice(25)

      // build captions after sort to keep everything together
      processedLegend = processedSeries[0].data.map(x => this.ExtractShortCaption(x.row.caption)) 

      this._consLegend = processedLegend
      this._consData = processedSeries
      this._changeCallback({ item: 'consequence', options: this.ConsOptions })
    }
  }
  
  get ConsOptions() {
    return {
      title: {
        text: 'Average Consequence (dollars)',
        x: 'center',
        textStyle: {
          fontWeight: 'normal',
          fontSize: 14
        }
      },
      tooltip : {
        trigger: 'item',
        formatter: (args) => {
          let tooltip = ''
          let name = args.data.row.type === ''  ? args.data.name : `${args.data.name} - ${args.data.row.type}`
          if (args.data)
            tooltip = `<p class="mb-0">${args.marker} ${name}: ${approx(args.data.value, {min10k:true})}</p>`

          return tooltip
        }
      },
      xAxis: {
        type: 'category',
        splitLine: {show:false},
        data: this._consLegend,
        axisLabel: {
          interval: 0,
          rotate: 30,
          hideOverlap: true,
          width: 120,
          overflow: 'truncate'
        }
      },
      yAxis: {
        type: 'value',
        name: 'Value',
        axisLabel : {
          formatter: (value) => {return approx(value)}
        }
      },
      series: this._consData
    };
  }

  BuildBaseSeries(name, color) {
    return {
      name: name,
      type:'bar',              
      data: [],
      itemStyle: {
        color: !color ? 'auto' : color
      },
      avoidLabelOverlap: false,
      animationType: 'scale',
      animationEasing: 'elasticOut',
      stack: 'Total'
    }
  }

  PrepMtbfData() {
    this._mtbfLegend = null
    this._mtbfData = null
    
    if(!this._data)
      return  [this.BuildBaseSeries('')]
    
    let allData = []
    for(const key of Object.keys(this._data)) {
      if(key === 'totalCount' || key === 'totalImprovementCount')
        continue

      if(this._data[key].count > 0 && this._data[key].mtbfAvg > 0)               
        allData.push({ name: this._data[key].caption, value:this._data[key].mtbfAvg,  row: this._data[key] })
    }

    allData.sort((a,b) => a.value === b.value ? 0 : (a.value > b.value ? -1 : 1))

    let aboveSeries = this.BuildBaseSeries('Better than library benchmark', '#66BB6A')
    let belowSeries = this.BuildBaseSeries('Worse than library benchmark', '#FFA000')
    let unassignedSeries = this.BuildBaseSeries('Classification not assigned', '#229dce')

    for(const dataItem of allData) {    
      if(dataItem.row.comparison === 'above') {
        aboveSeries.data.push(dataItem)
        belowSeries.data.push({ name: dataItem.name, value: '-' })
        unassignedSeries.data.push({ name: dataItem.name, value: '-' } )
      } else if(dataItem.row.comparison === 'below') {
        aboveSeries.data.push({ name: dataItem.name, value: '-' })
        belowSeries.data.push(dataItem)
        unassignedSeries.data.push({ name: dataItem.name, value: '-' } )
      } else {
        aboveSeries.data.push({ name: dataItem.name, value: '-' })
        belowSeries.data.push({ name: dataItem.name, value: '-' })
        unassignedSeries.data.push(dataItem)
      }        
    }

    // build captions after sort to keep everything together
    this._mtbfLegend = allData.map(x => this.ExtractShortCaption(x.row.caption)) 
    this._mtbfData = [aboveSeries, belowSeries, unassignedSeries]
    this._changeCallback({ item: 'mtbf', options: this.MtbfOptions })
  
  }
  
  get MtbfOptions() {
    return {
      title: {
        text: 'Average Characteristic Life (days)',
        x: 'center',
        textStyle: {
          fontWeight: 'normal',
          fontSize: 14
        }
      },
      tooltip : {
        trigger: 'item',
        formatter: (args) => {
          let tooltip = ''
          if (args.data){
            let name = args.data.row.type === ''  ? args.data.name : `${args.data.name} - ${args.data.row.type}`
            tooltip = `<p class="mb-0">${args.marker} ${name}: ${approx(args.data.value, {min10k:true})}</p>`
          } else if (args.length > 0){
            args.forEach(({ marker, seriesName, value, row }) => {
              value = value || [0, 0];
              let name = row.type === '' ? seriesName : `${seriesName} - ${row.type}`
              tooltip += `<p class="mb-0">${marker} ${name}: ${value[1]}</p>`;
            })
          }
          return tooltip
        }
      },
      xAxis: {
        type: 'category',
        splitLine: {show:false},
        data: this._mtbfLegend,
        axisLabel: {
          interval: 0,
          rotate: 30,
          hideOverlap: true,
          width: 120,
          overflow: 'truncate'
        }
      },
      yAxis: {
        type: 'value',
        name: 'Value',
        axisLabel : {
          formatter: (value) => {return approx(value)}
        }
      },
      series: this._mtbfData,
      legend: {    
        top: 30,    
        data: ['Better than library benchmark', 'Worse than library benchmark', 'Classification not assigned']      
      },
    }
  }

  PrepAssetCountData() {
    this._assetCountLegend = null
    this._preppedAssetCountData = null
    let processedData = []
    let processedLegend = []
    if(!this._data) {
      this._preppedAssetCountData = null
    } else {      
      for(const key of Object.keys(this._data)) {
        if(key === 'totalCount' || key === 'totalImprovementCount')
          continue

        if(this._data[key].count > 0)
          processedData.push({ name: this._data[key].caption, value: this._data[key].count, label: this.ExtractShortCaption(this._data[key].caption), row: this._data[key] })          
      }      
    }
    
    processedData.sort((a,b) => a.value === b.value ? 0 : (a.value > b.value ? -1 : 1))
    if(processedData.length > 25) {
      processedData.splice(25)
    }
     
    // build captions after sort to keep everything together
    processedLegend = processedData.map(x => x.label) 
    this._assetCountLegend = processedLegend
    this._preppedAssetCountData = processedData
    this._changeCallback({ item: 'assetCount', options: this.AssetCountOptions })
  }

  get AssetCountOptions() {
    return {      
      tooltip : {
        trigger: 'item',
        formatter: (args) => {
          let tooltip = ''
          if (args.data){            
            let name = args.data.row.type === ''  ? args.data.name : `${args.data.name} - ${args.data.row.type}` 
            tooltip = `<p class="mb-0">${args.marker} ${name}: ${approx(args.data.value, {min10k:true})}</p>`
          }
          return tooltip
        }
      },
      series : [{
        name: '',
        type:'pie',
        radius : '75%',
        center: ['50%', '50%'],
        data: this._preppedAssetCountData,
        avoidLabelOverlap: true,
        animationType: 'scale',
        animationEasing: 'elasticOut',
        itemStyle: {
          borderRadius: 4,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: true,
          formatter: (args) => {
            return args.data.label;
          }
        }
      }]
    }
  }

  ExtractShortCaption(val) {
    if(!val)
      return ''

    let parts = val.split(' - ')
    if(parts.length < 2)
      return val

    let result = ''
    for(let i = 1; i < parts.length; i++) {
      result += parts[i]
    }
    
    return result
  }

  ExecuteRequest() {
    this._workingCallback(true)
    axios.get(buildApiUrl('/api/assets/aggregate'))
      .then((d) => {        
        if(d && d.data && d.data.aggregateData) {
          this._data = d.data.aggregateData
          this.PrepData()
        }
      })
      .catch(err => {
        this._errorCallback(err, 'Error loading aggregate data.')
      })
      .finally(() => {
        this._workingCallback(false)
      })
  }
}

export default { AggregateHelper } 